(() => {
  function smoothstep(value) {
    return value * value * (3 - 2 * value);
  }

  function getConfig(root) {
    const isMobile = window.innerWidth < 750;
    const hotspotLeft = Number(root.dataset.hotspotLeft || 9) / 100;

    return {
      hotspotX(rect) {
        return rect.left + rect.width / 2 - rect.width * hotspotLeft;
      },
      core: Number(isMobile ? root.dataset.zoneCoreMobile || 62 : root.dataset.zoneCore || 76),
      fade: Number(isMobile ? root.dataset.zoneFadeMobile || 160 : root.dataset.zoneFade || 196),
    };
  }

  function computeBoost(distance, config) {
    const { core, fade } = config;

    if (distance >= fade) return 0;
    if (distance <= core) return 1;

    const t = 1 - (distance - core) / (fade - core);
    return smoothstep(Math.max(0, Math.min(1, t)));
  }

  /**
   * Keep enough identical groups so the strip always fills the viewport,
   * then shift by exactly one group width for a seamless infinite loop.
   */
  function ensureSeamlessLoop(marquee, track) {
    if (!marquee || !track) return;

    const groups = [...track.querySelectorAll('.cyber-ticker__group')];
    if (groups.length === 0) return;

    const template = groups[0];
    // Keep only the first group as source, remove previous clones
    groups.slice(1).forEach((group) => group.remove());

    const measure = () => template.getBoundingClientRect().width;
    let groupWidth = measure();
    if (!groupWidth) return;

    const viewport = marquee.clientWidth || 1;
    // Need at least 2 copies, and enough width to cover viewport + one full cycle
    const minTrack = viewport + groupWidth + 8;
    let copies = Math.max(2, Math.ceil(minTrack / groupWidth));

    for (let i = 1; i < copies; i += 1) {
      const clone = template.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      track.appendChild(clone);
    }

    // Re-measure after layout (fonts/icons can change width)
    groupWidth = measure();
    track.style.setProperty('--cyber-ticker-shift', `${Math.round(groupWidth)}px`);
  }

  function initCyberTicker(root) {
    if (!root || root.dataset.tickerReady === '1') return;
    root.dataset.tickerReady = '1';

    const marquee = root.querySelector('.cyber-ticker__marquee');
    const track = root.querySelector('.cyber-ticker__track');
    if (!marquee || !track) return;

    const syncLoop = () => ensureSeamlessLoop(marquee, track);
    syncLoop();

    // After fonts load, widths can change — re-sync once
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncLoop).catch(() => {});
    }

    let resizeTimer = null;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(syncLoop, 120);
    };
    window.addEventListener('resize', onResize);

    const refreshItems = () => [...marquee.querySelectorAll('.cyber-ticker__item')];
    let items = refreshItems();
    let rafId = null;

    const updateCenterHighlight = () => {
      items = refreshItems();
      const config = getConfig(root);
      const rect = marquee.getBoundingClientRect();
      const hotspotX = config.hotspotX(rect);

      items.forEach((item) => {
        if (item.classList.contains('is-hovered') || item.classList.contains('is-touched')) {
          return;
        }

        const itemRect = item.getBoundingClientRect();
        if (itemRect.right < rect.left || itemRect.left > rect.right) {
          item.style.setProperty('--neon-boost', '0');
          return;
        }

        const itemCenter = itemRect.left + itemRect.width / 2;
        const distance = Math.abs(hotspotX - itemCenter);
        const boost = computeBoost(distance, config);

        item.style.setProperty('--neon-boost', boost.toFixed(3));
      });

      rafId = requestAnimationFrame(updateCenterHighlight);
    };

    rafId = requestAnimationFrame(updateCenterHighlight);

    const bindItemInteractions = () => {
      refreshItems().forEach((item) => {
        if (item.dataset.tickerBound === '1') return;
        item.dataset.tickerBound = '1';

        item.addEventListener('mouseenter', () => {
          item.classList.add('is-hovered');
          item.style.setProperty('--neon-boost', '1');
        });

        item.addEventListener('mouseleave', () => {
          item.classList.remove('is-hovered');
        });

        item.addEventListener(
          'touchstart',
          () => {
            refreshItems().forEach((el) => el.classList.remove('is-touched'));
            item.classList.add('is-touched');
            item.style.setProperty('--neon-boost', '1');
          },
          { passive: true }
        );
      });
    };

    bindItemInteractions();
    // Re-bind after clones are added
    setTimeout(bindItemInteractions, 50);

    if (marquee.dataset.pauseOnHover !== undefined) {
      marquee.addEventListener('mouseenter', () => marquee.classList.add('is-paused'));
      marquee.addEventListener('mouseleave', () => marquee.classList.remove('is-paused'));
      marquee.addEventListener('touchstart', () => marquee.classList.add('is-paused'), { passive: true });
      marquee.addEventListener('touchend', () => marquee.classList.remove('is-paused'), { passive: true });
    }

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(root)) {
        cancelAnimationFrame(rafId);
        window.removeEventListener('resize', onResize);
      }
    });
  }

  const boot = () => {
    document.querySelectorAll('[data-cyber-ticker]').forEach((root) => {
      root.dataset.tickerReady = '';
      initCyberTicker(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-cyber-ticker]') || event.target.closest('[data-cyber-ticker]');
    if (root) {
      root.dataset.tickerReady = '';
      initCyberTicker(root);
    }
  });
})();
